<script setup lang="ts">
import AppIcon from '@/shared/ui/AppIcon/index';
import AppRating from '@/features/AppRating/index';
import { modifyDate } from '@/shared/utils/modifyTime';
import { useExpandableText } from '@/shared/composables/useExpandableText';
import { DEFAULT_USER_IMAGE } from '@/shared/constants/userConstants';
import { FEEDBACK_CONNECTED_TEXT } from '@/entities/feedback/lib/feedbackConstants';
import type { IFeedback } from '@/entities/feedback/model/types/feedback';

const props = defineProps<{ feedback: IFeedback }>();

const isMobile: boolean = useIsMobile();

const previewEl: Ref<HTMLElement | null> = ref(null);
const isShowFull: Ref<boolean> = ref(false);

const fullText: ComputedRef<string> = computed(() => props.feedback.fullText);

const { isOverflowing: isShowMoreButton } = useExpandableText(
  previewEl,
  isShowFull,
  fullText,
);

const fullName: ComputedRef<string> = computed(() => {
  const { surname, name, middlename } = props.feedback;
  return `${surname ?? ''} ${name ?? ''} ${middlename ?? ''}`.trim() || 'Аноним';
});

const replyAuthorName: ComputedRef<string> = computed(() => {
  const target = props.feedback.target;
  const companyName = target?.lawyer?.company?.name;
  if (companyName) return companyName;
  return (
    `${target?.surname ?? ''} ${target?.name ?? ''} ${target?.middlename ?? ''}`.trim() ||
    'Специалист'
  );
});

const replyAuthorAvatar: ComputedRef<string> = computed(
  () => props.feedback.target?.avatar || DEFAULT_USER_IMAGE,
);

const replyDate: ComputedRef<string> = computed(() =>
  modifyDate(props.feedback.updatedAt),
);

const showFullText = (): void => {
  isShowFull.value = true;
};
</script>

<template>
  <article :class="['feedback-card', { _mobile: isMobile }]">
    <div class="feedback-card__header">
      <div class="feedback-card__author">{{ fullName }}</div>
      <div class="feedback-card__date">
        Опубликовано:
        <span class="feedback-card__date-value">
          {{ modifyDate(feedback.createdAt) }}
        </span>
      </div>
      <div class="feedback-card__rate">
        <div class="feedback-card__rate-value">
          <AppRating disabled :model-value="feedback.rate" />
        </div>
        <span
          v-if="feedback.isConnected"
          class="feedback-card__rate-source"
          data-testid="feedback-card-connected"
        >
          {{ FEEDBACK_CONNECTED_TEXT }}
        </span>
      </div>
    </div>

    <div class="feedback-card__body">
      <div v-if="!isShowFull" ref="previewEl" class="feedback-card__preview">
        {{ feedback.fullText }}
      </div>
      <p v-else class="feedback-card__full-text">
        {{ feedback.fullText }}
      </p>

      <button
        v-if="!isShowFull && isShowMoreButton"
        type="button"
        class="feedback-card__more"
        aria-label="Подробнее"
        @click.prevent="showFullText"
      >
        Подробнее
        <AppIcon name="ArrowDownSmall" />
      </button>
    </div>

    <div
      v-if="feedback.review"
      class="feedback-card__reply"
      data-testid="feedback-card-reply"
    >
      <div class="feedback-card__reply-header">
        <NuxtImg
          class="feedback-card__reply-avatar"
          :src="replyAuthorAvatar"
          :alt="replyAuthorName"
          width="40"
          height="40"
          densities="x1 x2"
          loading="lazy"
        />
        <div class="feedback-card__reply-info">
          <div class="feedback-card__reply-author">
            {{ replyAuthorName }}
          </div>
          <div class="feedback-card__reply-label">
            Официальный ответ:
            <span class="feedback-card__reply-date">{{ replyDate }}</span>
          </div>
        </div>
      </div>
      <p class="feedback-card__reply-text">{{ feedback.review }}</p>
    </div>
  </article>
</template>

<style lang="less">
.feedback-card {
  &__author {
    font-size: 14px;
    line-height: 19px;
    font-weight: 600;
    color: @color-elephant;
    margin-bottom: 2px;
  }

  &__date {
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-dove-gray;
  }

  &__date-value {
    color: @color-elephant;
  }

  &__rate {
    display: inline-flex;
    align-items: center;
    margin-top: 10px;
  }

  &__rate-value {
    width: 107px;
    margin-right: 10px;

    .app-rating {
      justify-content: space-between;
    }

    svg {
      width: 15px;
      height: 15px;
    }
  }

  &__rate-source {
    font-size: @size-x3;
    line-height: @size-x4;
    font-weight: 500;
    color: @color-mountain-meadow;
  }

  &__body {
    position: relative;
    margin-top: @size-x3;
  }

  &__preview,
  &__full-text {
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
    color: @color-elephant;
    word-break: break-word;
    margin: 0;
  }

  &__preview {
    max-height: 66px;
    overflow: hidden;
  }

  &__more {
    position: absolute;
    right: 0;
    bottom: 0;
    display: inline-flex;
    align-items: center;
    gap: @size-x2;
    padding-left: 6px;
    background: #fff;
    font-size: @size-x4;
    line-height: 22px;
    font-weight: 400;
    color: @color-silver-chalice;
    letter-spacing: 0.41px;

    &::before {
      content: '...';
      margin-right: 2px;
      color: @color-elephant;
      box-shadow: 0 20px 12px 23px #fff;
    }

    svg path {
      stroke: @color-silver-chalice;
    }

    &:hover {
      color: @color-persimmon;

      svg path {
        stroke: @color-persimmon;
      }
    }
  }

  &__reply {
    margin-top: @size-x4;
    padding: @size-x4;
    background: @color-black-haze;
    border-radius: @main-size;

    &-header {
      display: flex;
      align-items: center;
    }

    &-avatar {
      flex-shrink: 0;
      width: @size-x10;
      height: @size-x10;
      object-fit: cover;
      border: 0.5px solid @color-silver-chalice;
      border-radius: @main-size;
      margin-right: 10px;
    }

    &-author {
      font-size: 14px;
      line-height: 18px;
      font-weight: 600;
      color: @color-elephant;
      margin-bottom: 2px;
    }

    &-label {
      font-size: @size-x3;
      line-height: @size-x4;
      font-weight: 500;
      color: @color-dove-gray;
    }

    &-date {
      color: @color-elephant;
    }

    &-text {
      margin: @size-x5 0 0 50px;
      font-size: 14px;
      line-height: 18px;
      font-weight: 500;
      word-break: break-word;
    }
  }

  &._mobile &__rate {
    margin-top: @size-x2;
  }

  &._mobile &__rate-value {
    max-width: 92px;
    margin-bottom: @main-size;

    svg {
      width: @size-x3;
      height: @size-x3;
    }
  }

  &._mobile &__rate-source {
    font-size: 10px;
    line-height: 13px;
  }

  &._mobile &__reply-header {
    align-items: flex-start;
  }

  &._mobile &__reply-text {
    margin-top: @size-x3;
  }
}
</style>
